/**
 * Welcome screen pre-auth : "Vous etes une marque ou un createur ?"
 *
 * Pourquoi cette page ?
 * --------------------
 * app.vogz.io etait pense pour les marques uniquement, mais comme on
 * communique partout sur "Vogz" sans distinction, beaucoup de createurs
 * arrivent sur ce portail et tombent dans un funnel qui n est pas pour
 * eux. On les ramene gentiment vers creator.vogz.io.
 *
 * Strategie :
 * - Affiche 2 cards : Marque (continue ici) / Createur (redirect)
 * - Stocke le choix dans un cookie 1 an pour ne plus reposer la question
 * - Bypass via query ?token=... (reset password) ou ?skip=1 (cas internes)
 */
import React, { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Cookies from 'js-cookie'

const ACCOUNT_TYPE_COOKIE = 'vogz-account-type'
const COOKIE_MAX_AGE_DAYS = 365

const CREATOR_PORTAL_URL =
  process.env.REACT_APP_CREATOR_PORTAL_URL || 'https://creator.vogz.io'

function Welcome() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const next = useMemo(() => {
    const raw = searchParams.get('next') || '/login'
    // Pas d open redirect : on n autorise que les paths internes.
    return raw.startsWith('/') ? raw : '/login'
  }, [searchParams])

  useEffect(() => {
    const stored = Cookies.get(ACCOUNT_TYPE_COOKIE)
    if (stored === 'brand' && !searchParams.get('force')) {
      navigate(next, { replace: true })
    }
  }, [navigate, next, searchParams])

  const handleBrand = () => {
    Cookies.set(ACCOUNT_TYPE_COOKIE, 'brand', { expires: COOKIE_MAX_AGE_DAYS })
    navigate(next, { replace: true })
  }

  const handleCreator = () => {
    Cookies.set(ACCOUNT_TYPE_COOKIE, 'creator', { expires: COOKIE_MAX_AGE_DAYS })
    const target = next.includes('signup') ? '/signup' : '/login'
    window.location.href = `${CREATOR_PORTAL_URL}${target}`
  }

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Vogz — Bienvenue</title>
      </Helmet>

      <main className="fixed inset-0 z-[100] overflow-y-auto bg-[#FAFAFB]">
        {/* Wrapper centre : on borne tout le contenu (header + grid) a la
            meme max-width pour que le titre et les cards soient bien alignes. */}
        <div className="min-h-full flex flex-col items-center justify-center px-6 py-12 md:py-20">
          <div style={{ width: '100%', maxWidth: 960 }}>
            <div className="flex flex-col items-center text-center mb-12">
              <img
                src="/assets/imgs/v2/text-logo.svg"
                alt="Vogz"
                className="h-7 mb-10"
              />
              <h1 className="text-[#0F172A] text-[30px] md:text-[38px] font-semibold leading-tight tracking-tight">
                Bienvenue sur Vogz
              </h1>
              <p className="text-[#64748B] text-base md:text-[17px] mt-3 max-w-xl">
                Pour vous orienter vers le bon espace, dites-nous qui vous êtes.
              </p>
            </div>

            {/* Grid 2 colonnes : width 100% forcee inline car certaines
                regles globales (Grommet/reset) empechaient le grid de
                s etirer correctement avec juste les classes Tailwind. */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 20,
                width: '100%',
              }}
              className="welcome-cards-grid"
            >
              <ChoiceCard
                onClick={handleBrand}
                accent="brand"
                title="Je suis une marque"
                description="Lancez des campagnes UGC et recevez des vidéos prêtes à diffuser."
                cta="Continuer"
              />
              <ChoiceCard
                onClick={handleCreator}
                accent="creator"
                title="Je suis un créateur"
                description="Postulez à des castings et faites-vous payer pour vos vidéos."
                cta="Accéder à mon espace"
                external
              />
            </div>
            <style>{`
              @media (max-width: 767px) {
                .welcome-cards-grid {
                  grid-template-columns: 1fr !important;
                }
              }
            `}</style>
          </div>
        </div>
      </main>
    </>
  )
}

interface ChoiceCardProps {
  onClick: () => void
  accent: 'brand' | 'creator'
  title: string
  description: string
  cta: string
  external?: boolean
}

function ChoiceCard({
  onClick,
  accent,
  title,
  description,
  cta,
  external = false,
}: ChoiceCardProps) {
  const isBrand = accent === 'brand'
  const accentColor = isBrand ? '#5777FF' : '#7E5BE0'
  const accentBg = isBrand ? '#EEF1FF' : '#F2EDFC'

  return (
    <button
      type="button"
      onClick={onClick}
      // width: 100% inline pour bypasser tout reset CSS user-agent
      // qui mettrait le button en width: max-content.
      style={{ width: '100%', minWidth: 0 }}
      className="group flex flex-col text-left bg-white border border-[#E5E7EB] rounded-2xl p-8 md:p-10 transition-all duration-200 hover:border-[#CBD5E1] hover:shadow-[0_12px_32px_-16px_rgba(15,23,42,0.18)] hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#5777FF]"
    >
      <span
        className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-6"
        style={{ backgroundColor: accentBg, color: accentColor }}
      >
        {isBrand ? <BrandIcon /> : <CreatorIcon />}
      </span>

      <span className="block text-[#0F172A] text-[20px] font-semibold mb-2">
        {title}
      </span>
      <span className="block text-[#64748B] text-[15px] leading-relaxed mb-8">
        {description}
      </span>

      <span
        className="mt-auto inline-flex items-center gap-1.5 text-[15px] font-semibold transition-transform group-hover:translate-x-0.5"
        style={{ color: accentColor }}
      >
        <span>{cta}</span>
        <span aria-hidden>{external ? '↗' : '→'}</span>
      </span>
    </button>
  )
}

function BrandIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path
        d="M3 21V8.5a2 2 0 0 1 1-1.73L11 3l7 3.77A2 2 0 0 1 19 8.5V21"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 21v-6h6v6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3 21h18"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}

function CreatorIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <rect
        x="3"
        y="6"
        width="13"
        height="12"
        rx="2.5"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path
        d="M16 10l5-3v10l-5-3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export default Welcome
